<script setup lang="ts">
/**
 * FILTRE PAR CATÉGORIE de la vitrine boutique.
 *
 * Extrait de `BoutiqueStepCatalogue` lors de la refonte : le catalogue portait
 * la grille, les états vides, le rappel panier, la barre mobile ET ce filtre —
 * cinq responsabilités dans un fichier qui approchait la limite `max-lines`.
 *
 * ⚠️ CE QUI CHANGE PAR RAPPORT À L'ANCIEN FILTRE (et pourquoi).
 *  - Le nombre de produits est affiché DANS chaque pastille. Un filtre qui ne
 *    dit pas ce qu'il contient se clique à l'aveugle : l'acheteur découvre
 *    l'état vide APRÈS coup. Avec le compte, il ne clique jamais dans le vide.
 *  - Hauteur `h-11` (44px) au lieu de `h-9` : cible tactile conforme sur
 *    mobile, où ce filtre est justement le plus utilisé.
 *  - L'état NON sélectionné est un aplat blanc PLEIN, pas un simple contour :
 *    sur le fond bleuté de la page, des pastilles en contour seul se lisaient
 *    comme du texte désactivé. (Règle `ui_no_bg_transparency` : aplats pleins.)
 *  - `scrollbar-hide` : dès que la rangée déborde (widget embarqué dans une
 *    colonne étroite, mobile), le navigateur peignait une barre de défilement
 *    grise sous les pastilles — le `pb-3` ne servait qu'à lui faire de la
 *    place. La rangée reste défilable au doigt et à la molette.
 */

const { t } = useI18n();
const props = defineProps<{
  /** Catégories distinctes, dans l'ordre d'apparition des produits. */
  categories: string[];
  /** Nombre de produits par catégorie — clé = nom exact de la catégorie. */
  counts: Record<string, number>;
  /** Nombre TOTAL de produits (pastille « Tout »). */
  totalCount: number;
}>();

const selected = defineModel<string | null>({ required: true });

/** Bascule : recliquer la catégorie active revient à « Tout ». */
function toggle(category: string) {
  selected.value = selected.value === category ? null : category;
}

/** Pastilles rendues : « Tout » puis chaque catégorie, avec son compte. */
const chips = computed(() => [
  { key: null as string | null, label: t("boutique.categoryFilter.tout"), count: props.totalCount },
  ...props.categories.map((category) => ({
    key: category as string | null,
    label: category,
    count: props.counts[category] ?? 0,
  })),
]);
</script>

<template>
  <div
    class="flex gap-2 overflow-x-auto scrollbar-hide pb-1 -mx-3 px-3 md:mx-0 md:px-0"
    role="group"
    :aria-label="$t('boutique.categoryFilter.ariaFiltrer')"
  >
    <button
      v-for="chip in chips"
      :key="chip.key ?? '__all__'"
      type="button"
      class="inline-flex shrink-0 items-center gap-2 h-11 px-4 rounded-full text-sm font-semibold border transition-colors"
      :class="selected === chip.key
        ? 'border-transparent'
        : 'border-gray-200 dark:border-[#262626] bg-white dark:bg-[#141414] text-[#505870] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#1e1e1e]'"
      :style="selected === chip.key
        ? { background: 'var(--accent, #212121)', color: 'var(--accent-text, #fff)' }
        : undefined"
      :aria-pressed="selected === chip.key"
      @click="chip.key === null ? (selected = null) : toggle(chip.key)"
    >
      {{ chip.label }}
      <!-- Compte en retrait typographique (`tabular-nums`, plus léger) : il
           informe sans concurrencer le nom de la catégorie. -->
      <span class="text-xs font-bold tabular-nums opacity-70">{{ chip.count }}</span>
    </button>
  </div>
</template>
